/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

const URL_PATTERN = /https?:\/\/[^\s]+|www\.[^\s]+/i;
const DOMAIN_LIKE_PATTERN = /[A-Za-z.,'-][A-Za-z0-9-]+\.[A-Za-z]{2,}(?:\.[A-Za-z]{2,})?/;
const NO_DIGIT_PATTERN = /^[^0-9]+$/;
const SIMPLE_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const FIRST_NAME_MAX_LENGTH = 40;
export const LAST_NAME_MAX_LENGTH = 80;

/** Drupal webform pattern for newsletter_sign_up_d2c_only first and last name. */
export const D2C_NAME_PATTERN = /^[a-zA-ZÀ-ÿñÑ\s\\]*$/;
export const D2C_INVALID_NAME_MESSAGE = 'Please enter valid name';

export const NEWSLETTER_SOURCE = {
    homepage: 'Homepage',
    checkout: 'Checkout',
    productDetailPage: 'Product Detail Page',
} as const;

export const INVALID_NAME_MESSAGE = 'Please provide a valid first and last name.';
export const INVALID_EMAIL_MESSAGE = 'Please enter a valid email address.';
export const GENERIC_SUBMIT_ERROR = 'We could not complete your request. Please try again.';
export const RECAPTCHA_FAILED_MESSAGE = 'reCAPTCHA verification failed. Please try again.';
export const PRIVACY_REQUIRED_MESSAGE = 'Please accept the privacy policy to continue.';

export function isConsentAccepted(value: unknown): boolean {
    return value === true || value === 'true' || value === 'on';
}

export function isValidPersonName(name: string): boolean {
    const trimmed = name.trim();
    if (!trimmed) {
        return false;
    }
    return !URL_PATTERN.test(trimmed) && !DOMAIN_LIKE_PATTERN.test(trimmed) && NO_DIGIT_PATTERN.test(trimmed);
}

export function isValidD2cName(name: string, maxLength: number): boolean {
    const trimmed = name.trim();
    if (!trimmed || trimmed.length > maxLength) {
        return false;
    }
    return D2C_NAME_PATTERN.test(trimmed);
}

export function isValidEmail(email: string): boolean {
    return SIMPLE_EMAIL_PATTERN.test(email.trim());
}
